/**
 * 预算对比组件
 */
import React from 'react';
import { Typography, Progress, Card, Tag, Space, Alert, Button } from 'antd';
import type { ProjectOverviewData } from '../../services/dashboardService';
import { calculateBudgetHealth, formatMoney, getBudgetTagColor } from '../../utils/budgetUtils';

const { Text, Title } = Typography;

interface BudgetComparisonProps {
  project: ProjectOverviewData;
  stats?: any;
  isMobile?: boolean;
  onViewDetail?: (type: 'worktime' | 'equipment' | 'material' | 'subcontract' | 'coordination') => void;
  onEditProject?: () => void;
}

const BudgetComparison: React.FC<BudgetComparisonProps> = ({
  project,
  stats,
  isMobile = false,
  onViewDetail,
  onEditProject,
}) => {
  const actualCost = stats?.summary?.totalProjectCost || project.actualCost || 0;
  const budgetUsageRate = typeof stats?.summary?.budgetUsageRate === 'number'
    ? stats.summary.budgetUsageRate
    : (project.budget > 0 ? (actualCost / project.budget) * 100 : 0);
  const budgetHealth = calculateBudgetHealth(project.budget, actualCost);
  const riskReasons = stats?.summary?.risk?.reasons || project.riskReasons || [];
  const riskTags = stats?.summary?.risk?.riskTags || project.riskTags || [];

  const actionButtons = [] as React.ReactNode[];

  if ((riskTags.includes('超预算') || budgetUsageRate > 100) && onViewDetail) {
    actionButtons.push(
      <Button key="overspend" size="small" onClick={() => onViewDetail('subcontract')}>
        查看超支来源
      </Button>
    );
  }

  if ((!project.budget || project.budget <= 0) && onEditProject) {
    actionButtons.push(
      <Button key="budget" size="small" onClick={onEditProject}>
        去补预算
      </Button>
    );
  }

  if (budgetUsageRate >= 70 && onViewDetail) {
    actionButtons.push(
      <Button key="material" size="small" onClick={() => onViewDetail('material')}>
        查看材料明细
      </Button>
    );
  }

  return (
    <div style={{ marginBottom: isMobile ? 16 : 24 }}>
      <Title level={5} style={{ marginBottom: 16, fontSize: isMobile ? 14 : 16, color: '#10171d' }}>
        预算对比与经营判断
      </Title>

      <Card
        size="small"
        style={{
          background: 'rgba(210, 218, 224, 0.5)',
          border: '1px solid rgba(16, 27, 35, 0.08)',
          borderRadius: 20,
          boxShadow: 'none'
        }}
        bodyStyle={{ padding: isMobile ? 14 : 18 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          <div>
            <Text style={{ fontSize: 12, color: '#667986' }}>预算总额</Text>
            <div>
              <Text strong style={{ fontSize: isMobile ? 16 : 18, color: '#10171d' }}>
                {formatMoney(project.budget, false)}
              </Text>
            </div>
          </div>
          <div>
            <Text style={{ fontSize: 12, color: '#667986' }}>累计统计成本</Text>
            <div>
              <Text strong style={{ fontSize: isMobile ? 16 : 18, color: budgetHealth.color }}>
                {formatMoney(actualCost, false)}
              </Text>
            </div>
          </div>
          <div>
            <Text style={{ fontSize: 12, color: '#667986' }}>剩余预算</Text>
            <div>
              <Text strong style={{ fontSize: isMobile ? 16 : 18, color: budgetHealth.remaining >= 0 ? '#52c41a' : '#ff4d4f' }}>
                {budgetHealth.remaining >= 0 ? '' : '-'}
                {formatMoney(Math.abs(budgetHealth.remaining), false)}
              </Text>
            </div>
          </div>
        </div>

        <Progress
          percent={Math.min(budgetUsageRate, 100)}
          strokeColor={budgetHealth.color}
          trailColor="rgba(16, 27, 35, 0.08)"
          format={() => `${budgetUsageRate.toFixed(1)}%`}
          style={{ marginBottom: 12 }}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <Text style={{ fontSize: isMobile ? 12 : 14, color: '#455763' }}>状态:</Text>
          <Tag color={getBudgetTagColor(budgetHealth.status)} style={{ borderRadius: 999 }}>
            {budgetHealth.icon} {budgetHealth.label}
          </Tag>
        </div>
        <Text style={{ fontSize: isMobile ? 11 : 12, display: 'block', marginTop: 4, color: '#667986' }}>
          {budgetHealth.message}
        </Text>

        {actionButtons.length > 0 && (
          <Space wrap size={8} style={{ marginTop: 12 }}>
            {actionButtons}
          </Space>
        )}

        {riskReasons.length > 0 && (
          <Space direction="vertical" style={{ width: '100%', marginTop: 12 }} size={8}>
            {riskReasons.map((reason: string, index: number) => (
              <Alert key={index} type="warning" showIcon message={reason} />
            ))}
          </Space>
        )}
      </Card>
    </div>
  );
};

export default BudgetComparison;
